// Pure predicate for whether CrossDeviceUploadsWatcher's fast-poll interval should be active.
// Kept in its own module (rather than inline in CrossDeviceUploadsWatcher.js) so a Jest test
// can import it without transitively loading Firebase Auth / axios / IndexedDB / etc. The
// component just consumes the predicate via `import { shouldFastPollFromState }`.
//
// Status literals are inlined intentionally — importing UploadClientStatus from packagesSlice
// would drag in the entire Redux slice + all its service dependencies, defeating the point.

const STATUS_UPLOADING = "UPLOADING";
const STATUS_PAUSED = "PAUSED";

// Server-side statuses where a *remote* row is still meaningfully actionable from another
// device's perspective — the user can take it over, resume it, or watch bytes flow. Once the
// row advances past these (READY_TO_UPLOAD_TO_PROVIDER onwards), the upload is effectively
// done as far as the user is concerned: bytes are transferred and the server pipeline
// (S3, CatDV, etc.) is finalizing on its own. Showing a "100% finalizing" spectator card for
// that duration is misleading UX (users see the card sitting for the full server-side
// pipeline and think their upload is stuck), so we filter it out both in the UI list and in
// the fast-poll trigger. See CrossDeviceUploadsWatcher.test.js.
const REMOTE_ACTIONABLE_STATUSES = new Set([
  "PENDING_CHUNKS",
  "CHUNK_UPLOAD_IN_PROGRESS",
  "PAUSED",
]);

export const visibleRemoteUploads = (crossDeviceItems) =>
  (crossDeviceItems || []).filter(
    (u) => u && !u.ownedByCurrentSession && REMOTE_ACTIONABLE_STATUSES.has(u.status),
  );

export const shouldFastPollFromState = (activeUploads, crossDeviceItems) => {
  const hasRemoteUploads = visibleRemoteUploads(crossDeviceItems).length > 0;
  const hasLocalLiveUpload = (activeUploads || []).some(
    (u) => u?.status === STATUS_UPLOADING || u?.status === STATUS_PAUSED,
  );
  return hasRemoteUploads || hasLocalLiveUpload;
};
